import axios, { AxiosError } from "axios"
import { AnyAction } from "redux"
import { Dispatch, RNBThunkAction } from "../store.config"
import { isAxiosError } from "../../utils/asyncTools"
import { ToastErrorProps } from "./merchantCodes.ducks"
import {
  getUserCompaniesThunk,
  removeCompanyImplantation,
  updateCompanyImplantations,
} from "./companies.ducks"
import { getCurrentUserImplantationsThunk } from "./user.ducks"

export interface ImplantationItem {
  id: number
  name: string
}
export interface Implantation extends ImplantationItem {
  companyIds: number[]
  userIds: number[]
}

export interface ImplantationEvent {
  id: number
  event_type:
    | "create_implantation"
    | "update_implantation"
    | "delete_implantation"
    | "create_company_permission"
    | "delete_company_permission"
  company_id: number
  email: string
  date: string
  metadata: string
}

const enum ImplantationsActionsEnum {
  GET_IMPLANTATIONS_ATTEMPT = "IMPLANTATIONS/getImplantationsAttempt",
  GET_IMPLANTATIONS_SUCCESS = "IMPLANTATIONS/getImplantationsSuccess",
  GET_IMPLANTATIONS_ERROR = "IMPLANTATIONS/getImplantationsError",

  GET_IMPLANTATION_EVENTS_ATTEMPT = "IMPLANTATIONS/getImplantationEventsAttempt",
  GET_IMPLANTATION_EVENTS_SUCCESS = "IMPLANTATIONS/getImplantationEventsSuccess",
  GET_IMPLANTATION_EVENTS_ERROR = "IMPLANTATIONS/getImplantationEventsError",

  GET_COMPANY_IMPLANTATION_EVENTS_ATTEMPT = "IMPLANTATIONS/getCompanyImplantationEventsAttempt",
  GET_COMPANY_IMPLANTATION_EVENTS_SUCCESS = "IMPLANTATIONS/getCompanyImplantationEventsSuccess",
  GET_COMPANY_IMPLANTATION_EVENTS_ERROR = "IMPLANTATIONS/getCompanyImplantationEventsError",

  CREATE_IMPLANTATION_ATTEMPT = "IMPLANTATIONS/createImplantationAttempt",
  CREATE_IMPLANTATION_SUCCESS = "IMPLANTATIONS/createImplantationSuccess",
  CREATE_IMPLANTATION_ERROR = "IMPLANTATIONS/createImplantationError",

  UPDATE_IMPLANTATION_ATTEMPT = "IMPLANTATIONS/updateImplantationAttempt",
  UPDATE_IMPLANTATION_SUCCESS = "IMPLANTATIONS/updateImplantationSuccess",
  UPDATE_IMPLANTATION_ERROR = "IMPLANTATIONS/updateImplantationError",

  DELETE_IMPLANTATION_ATTEMPT = "IMPLANTATIONS/deleteImplantationAttempt",
  DELETE_IMPLANTATION_SUCCESS = "IMPLANTATIONS/deleteImplantationSuccess",
  DELETE_IMPLANTATION_ERROR = "IMPLANTATIONS/deleteImplantationError",

  UPDATE_COMPANY_IMPLANTATIONS_ATTEMPT = "IMPLANTATIONS/updateCompanyImplantationsAttempt",
  UPDATE_COMPANY_IMPLANTATIONS_SUCCESS = "IMPLANTATIONS/updateCompanyImplantationsSuccess",
  UPDATE_COMPANY_IMPLANTATIONS_ERROR = "IMPLANTATIONS/updateCompanyImplantationsError",

  REMOVE_COMPANY_IMPLANTATION_ATTEMPT = "IMPLANTATIONS/removeCompanyImplantationAttempt",
  REMOVE_COMPANY_IMPLANTATION_SUCCESS = "IMPLANTATIONS/removeCompanyImplantationSuccess",
  REMOVE_COMPANY_IMPLANTATION_ERROR = "IMPLANTATIONS/removeCompanyImplantationError",
}

export const getImplantationsAttemptAction = () =>
  ({ type: ImplantationsActionsEnum.GET_IMPLANTATIONS_ATTEMPT } as const)
export const getImplantationsSuccessAction = (payload: Implantation[]) =>
  ({
    type: ImplantationsActionsEnum.GET_IMPLANTATIONS_SUCCESS,
    payload,
  } as const)
export const getImplantationsErrorAction = () =>
  ({ type: ImplantationsActionsEnum.GET_IMPLANTATIONS_ERROR } as const)

export const getImplantationEventsAttemptAction = () =>
  ({ type: ImplantationsActionsEnum.GET_IMPLANTATION_EVENTS_ATTEMPT } as const)
export const getImplantationEventsSuccessAction = (
  payload: ImplantationEvent[]
) =>
  ({
    type: ImplantationsActionsEnum.GET_IMPLANTATION_EVENTS_SUCCESS,
    payload,
  } as const)
export const getImplantationEventsErrorAction = () =>
  ({ type: ImplantationsActionsEnum.GET_IMPLANTATION_EVENTS_ERROR } as const)

export const getCompanyImplantationEventsAttemptAction = () =>
  ({
    type: ImplantationsActionsEnum.GET_COMPANY_IMPLANTATION_EVENTS_ATTEMPT,
  } as const)
export const getCompanyImplantationEventsSuccessAction = (
  payload: ImplantationEvent[]
) =>
  ({
    type: ImplantationsActionsEnum.GET_COMPANY_IMPLANTATION_EVENTS_SUCCESS,
    payload,
  } as const)
export const getCompanyImplantationEventsErrorAction = () =>
  ({
    type: ImplantationsActionsEnum.GET_COMPANY_IMPLANTATION_EVENTS_ERROR,
  } as const)

export const createImplantationAttemptAction = () =>
  ({ type: ImplantationsActionsEnum.CREATE_IMPLANTATION_ATTEMPT } as const)
export const createImplantationSuccessAction = (payload: Implantation) =>
  ({
    type: ImplantationsActionsEnum.CREATE_IMPLANTATION_SUCCESS,
    withToast: true,
    toasterType: "success",
    message: {
      titleKey: "fiduciary.implantations.success.create-implantation.title",
      bodyKey: "fiduciary.implantations.success.create-implantation.body",
    },
  } as const)
export const createImplantationErrorAction = (error: ToastErrorProps) =>
  ({
    type: ImplantationsActionsEnum.CREATE_IMPLANTATION_ERROR,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey: error.titleKey,
      bodyKey: error.bodyKey,
      bodyValues: error.bodyValues,
    },
  } as const)

export const updateImplantationAttemptAction = () =>
  ({ type: ImplantationsActionsEnum.UPDATE_IMPLANTATION_ATTEMPT } as const)
export const updateImplantationSuccessAction = () =>
  ({
    type: ImplantationsActionsEnum.UPDATE_IMPLANTATION_SUCCESS,
    withToast: true,
    toasterType: "success",
    message: {
      titleKey: "fiduciary.implantations.success.update-implantation.title",
      bodyKey: "fiduciary.implantations.success.update-implantation.body",
    },
  } as const)
export const updateImplantationErrorAction = (error: ToastErrorProps) =>
  ({
    type: ImplantationsActionsEnum.UPDATE_IMPLANTATION_ERROR,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey: error.titleKey,
      bodyKey: error.bodyKey,
      bodyValues: error.bodyValues,
    },
  } as const)

export const deleteImplantationAttemptAction = () =>
  ({ type: ImplantationsActionsEnum.DELETE_IMPLANTATION_ATTEMPT } as const)
export const deleteImplantationSuccessAction = (implantationId: number) =>
  ({
    type: ImplantationsActionsEnum.DELETE_IMPLANTATION_SUCCESS,
    withToast: true,
    toasterType: "success",
    message: {
      titleKey: "fiduciary.implantations.success.delete-implantation.title",
      bodyKey: "fiduciary.implantations.success.delete-implantation.body",
    },
    implantationId,
  } as const)
export const deleteImplantationErrorAction = (error: ToastErrorProps) =>
  ({
    type: ImplantationsActionsEnum.DELETE_IMPLANTATION_ERROR,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey: error.titleKey,
      bodyKey: error.bodyKey,
      bodyValues: error.bodyValues,
    },
  } as const)

export const updateCompanyImplantationsAttemptAction = () =>
  ({
    type: ImplantationsActionsEnum.UPDATE_COMPANY_IMPLANTATIONS_ATTEMPT,
  } as const)
export const updateCompanyImplantationsSuccessAction = () =>
  ({
    type: ImplantationsActionsEnum.UPDATE_COMPANY_IMPLANTATIONS_SUCCESS,
    withToast: true,
    toasterType: "success",
    message: {
      titleKey:
        "fiduciary.implantations.success.update-company-implantation.title",
      bodyKey:
        "fiduciary.implantations.success.update-company-implantation.body",
    },
  } as const)
export const updateCompanyImplantationsErrorAction = (error: ToastErrorProps) =>
  ({
    type: ImplantationsActionsEnum.UPDATE_COMPANY_IMPLANTATIONS_ERROR,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey: error.titleKey,
      bodyKey: error.bodyKey,
      bodyValues: error.bodyValues,
    },
  } as const)

export const removeCompanyImplantationAttemptAction = () =>
  ({
    type: ImplantationsActionsEnum.REMOVE_COMPANY_IMPLANTATION_ATTEMPT,
  } as const)
export const removeCompanyImplantationSuccessAction = () =>
  ({
    type: ImplantationsActionsEnum.REMOVE_COMPANY_IMPLANTATION_SUCCESS,
    withToast: true,
    toasterType: "success",
    message: {
      titleKey:
        "fiduciary.implantations.success.delete-company-implantation.title",
      bodyKey:
        "fiduciary.implantations.success.delete-company-implantation.body",
    },
  } as const)
export const removeCompanyImplantationErrorAction = (error: ToastErrorProps) =>
  ({
    type: ImplantationsActionsEnum.REMOVE_COMPANY_IMPLANTATION_ERROR,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey: error.titleKey,
      bodyKey: error.bodyKey,
      bodyValues: error.bodyValues,
    },
  } as const)

type ImplantationsActionType = ReturnType<
  | typeof getImplantationsAttemptAction
  | typeof getImplantationsSuccessAction
  | typeof getImplantationsErrorAction
  | typeof getImplantationEventsAttemptAction
  | typeof getImplantationEventsSuccessAction
  | typeof getImplantationEventsErrorAction
  | typeof getCompanyImplantationEventsAttemptAction
  | typeof getCompanyImplantationEventsSuccessAction
  | typeof getCompanyImplantationEventsErrorAction
  | typeof createImplantationAttemptAction
  | typeof createImplantationSuccessAction
  | typeof createImplantationErrorAction
  | typeof updateImplantationAttemptAction
  | typeof updateImplantationSuccessAction
  | typeof updateImplantationErrorAction
  | typeof deleteImplantationAttemptAction
  | typeof deleteImplantationSuccessAction
  | typeof deleteImplantationErrorAction
  | typeof updateCompanyImplantationsAttemptAction
  | typeof updateCompanyImplantationsSuccessAction
  | typeof updateCompanyImplantationsErrorAction
  | typeof removeCompanyImplantationAttemptAction
  | typeof removeCompanyImplantationSuccessAction
  | typeof removeCompanyImplantationErrorAction
>

export interface ImplantationsState {
  implantations: Implantation[]
  implantationEvents: ImplantationEvent[]
  companyImplantationEvents: ImplantationEvent[]
  getImplantationsStatus: "IDLE" | "LOADING" | "SUCCESS" | "ERROR"
  getImplantationEventsStatus: "IDLE" | "LOADING" | "SUCCESS" | "ERROR"
  getCompanyImplantationEventsStatus: "IDLE" | "LOADING" | "SUCCESS" | "ERROR"
  createImplantationStatus: "IDLE" | "LOADING" | "SUCCESS" | "ERROR"
  updateImplantationStatus: "IDLE" | "LOADING" | "SUCCESS" | "ERROR"
  deleteImplantationStatus: "IDLE" | "LOADING" | "SUCCESS" | "ERROR"
  updateCompanyImplantationsStatus: "IDLE" | "LOADING" | "SUCCESS" | "ERROR"
  removeCompanyImplantationStatus: "IDLE" | "LOADING" | "SUCCESS" | "ERROR"
}

export const implantationsInitialState: ImplantationsState = {
  implantations: [],
  implantationEvents: [],
  companyImplantationEvents: [],
  getImplantationsStatus: "IDLE",
  getImplantationEventsStatus: "IDLE",
  getCompanyImplantationEventsStatus: "IDLE",
  createImplantationStatus: "IDLE",
  updateImplantationStatus: "IDLE",
  deleteImplantationStatus: "IDLE",
  updateCompanyImplantationsStatus: "IDLE",
  removeCompanyImplantationStatus: "IDLE",
}

export function implantationsReducer(
  state = implantationsInitialState,
  action: ImplantationsActionType
): ImplantationsState {
  switch (action.type) {
    case ImplantationsActionsEnum.GET_IMPLANTATIONS_ATTEMPT: {
      return { ...state, getImplantationsStatus: "LOADING" }
    }
    case ImplantationsActionsEnum.GET_IMPLANTATIONS_SUCCESS: {
      return {
        ...state,
        getImplantationsStatus: "SUCCESS",
        implantations: action.payload,
      }
    }
    case ImplantationsActionsEnum.GET_IMPLANTATIONS_ERROR: {
      return { ...state, getImplantationsStatus: "ERROR" }
    }
    case ImplantationsActionsEnum.GET_IMPLANTATION_EVENTS_ATTEMPT: {
      return { ...state, getImplantationEventsStatus: "LOADING" }
    }
    case ImplantationsActionsEnum.GET_IMPLANTATION_EVENTS_SUCCESS: {
      return {
        ...state,
        getImplantationEventsStatus: "SUCCESS",
        implantationEvents: action.payload,
      }
    }
    case ImplantationsActionsEnum.GET_IMPLANTATION_EVENTS_ERROR: {
      return { ...state, getImplantationEventsStatus: "ERROR" }
    }
    case ImplantationsActionsEnum.GET_COMPANY_IMPLANTATION_EVENTS_ATTEMPT: {
      return { ...state, getCompanyImplantationEventsStatus: "LOADING" }
    }
    case ImplantationsActionsEnum.GET_COMPANY_IMPLANTATION_EVENTS_SUCCESS: {
      return {
        ...state,
        getCompanyImplantationEventsStatus: "SUCCESS",
        companyImplantationEvents: action.payload,
      }
    }
    case ImplantationsActionsEnum.GET_COMPANY_IMPLANTATION_EVENTS_ERROR: {
      return { ...state, getCompanyImplantationEventsStatus: "ERROR" }
    }
    case ImplantationsActionsEnum.CREATE_IMPLANTATION_ATTEMPT: {
      return { ...state, createImplantationStatus: "LOADING" }
    }
    case ImplantationsActionsEnum.CREATE_IMPLANTATION_SUCCESS: {
      return { ...state, createImplantationStatus: "SUCCESS" }
    }
    case ImplantationsActionsEnum.CREATE_IMPLANTATION_ERROR: {
      return { ...state, createImplantationStatus: "ERROR" }
    }
    case ImplantationsActionsEnum.UPDATE_IMPLANTATION_ATTEMPT: {
      return { ...state, updateImplantationStatus: "LOADING" }
    }
    case ImplantationsActionsEnum.UPDATE_IMPLANTATION_SUCCESS: {
      return { ...state, updateImplantationStatus: "SUCCESS" }
    }
    case ImplantationsActionsEnum.UPDATE_IMPLANTATION_ERROR: {
      return { ...state, updateImplantationStatus: "ERROR" }
    }
    case ImplantationsActionsEnum.DELETE_IMPLANTATION_ATTEMPT: {
      return { ...state, deleteImplantationStatus: "LOADING" }
    }
    case ImplantationsActionsEnum.DELETE_IMPLANTATION_SUCCESS: {
      return {
        ...state,
        implantations: [
          ...state.implantations.filter((i) => i.id !== action.implantationId),
        ],
        deleteImplantationStatus: "SUCCESS",
      }
    }
    case ImplantationsActionsEnum.DELETE_IMPLANTATION_ERROR: {
      return { ...state, deleteImplantationStatus: "ERROR" }
    }
    case ImplantationsActionsEnum.UPDATE_COMPANY_IMPLANTATIONS_ATTEMPT: {
      return { ...state, updateCompanyImplantationsStatus: "LOADING" }
    }
    case ImplantationsActionsEnum.UPDATE_COMPANY_IMPLANTATIONS_SUCCESS: {
      return { ...state, updateCompanyImplantationsStatus: "SUCCESS" }
    }
    case ImplantationsActionsEnum.UPDATE_COMPANY_IMPLANTATIONS_ERROR: {
      return { ...state, updateCompanyImplantationsStatus: "ERROR" }
    }

    case ImplantationsActionsEnum.REMOVE_COMPANY_IMPLANTATION_ATTEMPT: {
      return { ...state, removeCompanyImplantationStatus: "LOADING" }
    }
    case ImplantationsActionsEnum.REMOVE_COMPANY_IMPLANTATION_SUCCESS: {
      return { ...state, removeCompanyImplantationStatus: "SUCCESS" }
    }
    case ImplantationsActionsEnum.REMOVE_COMPANY_IMPLANTATION_ERROR: {
      return { ...state, removeCompanyImplantationStatus: "ERROR" }
    }

    default:
      return { ...state }
  }
}

const getImplantationErrorMessages = (
  error: AxiosError | Error,
  defaultKey = "default"
) => {
  let key = defaultKey
  let bodyValues = {}

  if (
    isAxiosError(error) &&
    error.response !== undefined &&
    error.response.data
  ) {
    const errorMsg = JSON.stringify(error.response.data)

    if (errorMsg.includes("an implantation with this name already exists")) {
      key = "name-already-exist"
    }
  }

  return {
    titleKey: `fiduciary.implantations.error.${key}.title`,
    bodyKey: `fiduciary.implantations.error.${key}.body`,
    bodyValues,
  }
}

export const getImplantationsThunk =
  (fiduciaryId: number) => (dispatch: Dispatch<ImplantationsActionType>) => {
    dispatch(getImplantationsAttemptAction())
    return axios
      .get<Implantation[]>(`/fiduciaries/${fiduciaryId}/get_implantations`)
      .then((response) => {
        dispatch(getImplantationsSuccessAction(response.data))
      })
      .catch(() => {
        dispatch(getImplantationsErrorAction())
      })
  }

export const getImplantationEventsThunk =
  (fiduciaryId: number) => (dispatch: Dispatch<ImplantationsActionType>) => {
    dispatch(getImplantationEventsAttemptAction())
    return axios
      .get<ImplantationEvent[]>(
        `/fiduciaries/${fiduciaryId}/get_implantation_events`
      )
      .then((response) => {
        dispatch(getImplantationEventsSuccessAction(response.data))
      })
      .catch(() => {
        dispatch(getImplantationEventsErrorAction())
      })
  }

export const getCompanyImplantationEventsThunk =
  (companyId: number) => (dispatch: Dispatch<ImplantationsActionType>) => {
    dispatch(getCompanyImplantationEventsAttemptAction())
    return axios
      .get<ImplantationEvent[]>(
        `/companies/${companyId}/company_implantation_events`
      )
      .then((response) => {
        dispatch(getCompanyImplantationEventsSuccessAction(response.data))
      })
      .catch(() => {
        dispatch(getCompanyImplantationEventsErrorAction())
      })
  }

export const createImplantationThunk =
  (
    fiduciaryId: number,
    name: string,
    company_ids: number[],
    user_ids: number[]
  ) =>
  (dispatch: Dispatch<ImplantationsActionType | RNBThunkAction>) => {
    dispatch(createImplantationAttemptAction())
    return axios
      .post<Implantation>(`/fiduciaries/${fiduciaryId}/create_implantation`, {
        name,
        company_ids,
        user_ids,
      })
      .then((response) => {
        dispatch(createImplantationSuccessAction(response.data))
        dispatch(getImplantationsThunk(fiduciaryId))
        dispatch(getImplantationEventsThunk(fiduciaryId))
        dispatch(getUserCompaniesThunk())
        dispatch(getCurrentUserImplantationsThunk())
      })
      .catch((error: AxiosError) => {
        dispatch(
          createImplantationErrorAction(getImplantationErrorMessages(error))
        )
      })
  }

export const updateImplantationThunk =
  (
    fiduciaryId: number,
    implantation_id: number,
    name: string,
    company_ids: number[],
    user_ids: number[]
  ) =>
  (dispatch: Dispatch<ImplantationsActionType | RNBThunkAction>) => {
    dispatch(updateImplantationAttemptAction())
    return axios
      .put<Implantation>(`/fiduciaries/${fiduciaryId}/update_implantation`, {
        implantation_id,
        name,
        company_ids,
        user_ids,
      })
      .then(() => {
        dispatch(updateImplantationSuccessAction())
        dispatch(getImplantationsThunk(fiduciaryId))
        dispatch(getImplantationEventsThunk(fiduciaryId))
        dispatch(getUserCompaniesThunk())
      })
      .catch((error: AxiosError) => {
        dispatch(
          updateImplantationErrorAction(getImplantationErrorMessages(error))
        )
      })
  }

interface DeleteImplantation {
  implantationId: number
}
export const deleteImplantationThunk =
  (fiduciaryId: number, implantationId: number) =>
  (dispatch: Dispatch<ImplantationsActionType | RNBThunkAction>) => {
    dispatch(deleteImplantationAttemptAction())
    return axios
      .delete<DeleteImplantation>(
        `/fiduciaries/${fiduciaryId}/delete_implantation`,
        {
          params: { implantation_id: implantationId },
        }
      )
      .then(() => {
        dispatch(deleteImplantationSuccessAction(implantationId))
        dispatch(getImplantationEventsThunk(fiduciaryId))
        dispatch(getUserCompaniesThunk())
        dispatch(getCurrentUserImplantationsThunk())
      })
      .catch((error: AxiosError) => {
        dispatch(
          deleteImplantationErrorAction(getImplantationErrorMessages(error))
        )
      })
  }

export const updateCompanyImplantationsThunk =
  (companyId: number, implantations: ImplantationItem[]) =>
  (
    dispatch: Dispatch<ImplantationsActionType | RNBThunkAction | AnyAction>
  ) => {
    dispatch(updateCompanyImplantationsAttemptAction())
    return axios
      .put<Implantation>(
        `/companies/${companyId}/update_company_implantations`,
        {
          implantation_ids: implantations.map((i) => i.id),
        }
      )
      .then(() => {
        dispatch(updateCompanyImplantationsSuccessAction())
        dispatch(updateCompanyImplantations(companyId, implantations))
        dispatch(getCompanyImplantationEventsThunk(companyId))
      })
      .catch((error: AxiosError) => {
        dispatch(
          updateCompanyImplantationsErrorAction(
            getImplantationErrorMessages(error)
          )
        )
      })
  }

export const removeCompanyImplantationThunk =
  (companyId: number, implantationId: number) =>
  (
    dispatch: Dispatch<ImplantationsActionType | RNBThunkAction | AnyAction>
  ) => {
    dispatch(removeCompanyImplantationAttemptAction())
    return axios
      .put<Implantation>(
        `/companies/${companyId}/remove_company_implantation`,
        {
          implantation_id: implantationId,
        }
      )
      .then(() => {
        dispatch(removeCompanyImplantationSuccessAction())
        dispatch(removeCompanyImplantation(companyId, implantationId))
        dispatch(getCompanyImplantationEventsThunk(companyId))
      })
      .catch((error: AxiosError) => {
        dispatch(
          removeCompanyImplantationErrorAction(
            getImplantationErrorMessages(error)
          )
        )
      })
  }
